import { useState } from "react";
import { toast } from "sonner";
import { X, PhoneCall } from "lucide-react";
import { postWebsiteLead } from "@/lib/leadCapture";
import { sanitizeNameInput } from "@/lib/formInput";
import PhoneInput, { isValidPhone } from "@/components/ui-shared/PhoneInput";

const CourseInterestModal = ({ open, onClose, courseTitle, productTitle }) => {
    const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
    const [loading, setLoading] = useState(false);
    if (!open) return null;
    const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });
    const onSubmit = async (e) => {
        e.preventDefault();
        if (!form.name.trim() || !form.email.trim() || !form.company.trim()) {
            toast.error("Please fill in all the fields.");
            return;
        }
        if (!isValidPhone(form.phone)) {
            toast.error("Please enter a valid 10-digit phone number.");
            return;
        }
        setLoading(true);
        const ok = await postWebsiteLead({ ...form, course: courseTitle, product: productTitle }, "course-interest");
        setLoading(false);
        if (ok) {
            toast.success("Thanks! Our team will reach out shortly to help you get started.");
            setForm({ name: "", email: "", phone: "", company: "" });
            onClose();
        } else {
            toast.error("Unable to submit right now. Please try again in a moment.");
        }
    };
    return (
        <div data-testid="course-interest-modal" className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm animate-fade-up" onClick={onClose}>
            <form onClick={(e) => e.stopPropagation()} onSubmit={onSubmit} className="w-full max-w-lg sxe-form-card rounded-2xl p-6 md:p-8 relative">
                <button type="button" onClick={onClose} aria-label="Close" data-testid="course-interest-modal-close" className="absolute top-4 right-4 w-9 h-9 rounded-full border border-[#150826]/10 text-[#150826] hover:border-[#A100FF]/50 hover:bg-[#A100FF]/5 flex items-center justify-center"><X className="w-4 h-4" /></button>
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#A100FF] to-[#7B00CC] flex items-center justify-center"><PhoneCall className="w-6 h-6 text-white" /></div>
                <h3 className="font-display text-[#150826] text-2xl md:text-3xl font-medium tracking-tight mt-5">Talk to our team</h3>
                {courseTitle && <p className="text-[#7B00CC] text-sm mt-1">{courseTitle}</p>}
                <p className="text-[#6B5980] text-sm mt-2">Share a few details and one of our learning advisors will reach out to walk you through this program.</p>
                <div className="grid md:grid-cols-2 gap-3 mt-6">
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Full Name*</label>
                        <input data-testid="course-interest-input-name" name="name" value={form.name} onChange={(e) => setForm({ ...form, name: sanitizeNameInput(e.target.value) })} className="sxe-form-input mt-1" placeholder="Your name" required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Business Email*</label>
                        <input data-testid="course-interest-input-email" name="email" type="email" value={form.email} onChange={onChange} className="sxe-form-input mt-1" placeholder="you@company.com" required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Phone Number*</label>
                        <PhoneInput testId="course-interest-input-phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} required />
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-widest text-[#6B5980] font-display">Company Name*</label>
                        <input data-testid="course-interest-input-company" name="company" value={form.company} onChange={onChange} className="sxe-form-input mt-1" placeholder="Company name" required />
                    </div>
                </div>
                <button type="submit" disabled={loading} data-testid="course-interest-submit-btn" className="btn-primary w-full mt-6 disabled:opacity-60">
                    {loading ? "Submitting..." : (<>Talk to Support <PhoneCall className="w-4 h-4" /></>)}
                </button>
            </form>
        </div>
    );
};

export default CourseInterestModal;
